import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Search, X } from "lucide-react";

import { buildSearchIndex, searchIndex } from "@/lib/searchIndex";

const Section = ({ title, children }) => (
  <div className="mb-6">
    <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-3">{title}</p>
    <div className="space-y-2">{children}</div>
  </div>
);

const MenuLink = ({ title, href, onNavigate }) => (
  <Link
    to={href}
    onClick={onNavigate}
    className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/5 text-[#EDEDED]"
  >
    <span className="text-sm">{title}</span>
    <ChevronRight className="w-4 h-4 text-[#A1A1AA]" />
  </Link>
);

const MenuDrawer = ({ open, onOpenChange }) => {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const items = useMemo(() => buildSearchIndex(), []);
  const results = useMemo(() => searchIndex(query, items), [query, items]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const close = () => onOpenChange(false);
  const go = (href) => {
    close();
    navigate(href);
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-black/70" onClick={close} />
      <div className="fixed left-0 top-0 bottom-0 w-full max-w-md bg-[#050505] border-r border-white/10 flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-[#050505] border-b border-white/10 p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[#EDEDED] text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
              Menu
            </p>
            <button onClick={close} className="text-[#A1A1AA] hover:text-[#EDEDED]">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-[#A1A1AA]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Breedling (dog, stallion, groomer…)"
              className="bg-transparent border-none text-[#EDEDED] placeholder:text-[#71717A] w-full focus:outline-none text-sm"
              autoFocus
            />
          </div>

          {query.trim() && (
            <div className="mt-3 rounded-xl border border-white/10 overflow-hidden">
              {results.length === 0 ? (
                <div className="px-3 py-3 text-sm text-[#A1A1AA]">No results</div>
              ) : (
                results.map((r) => (
                  <button
                    key={`${r.group}-${r.href}`}
                    onClick={() => go(r.href)}
                    className="w-full text-left px-3 py-3 hover:bg-white/5"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#EDEDED]">{r.title}</p>
                        <p className="text-xs text-[#71717A]">{r.group}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#A1A1AA]" />
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Directory */}
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          <Section title="Browse by Species">
            <MenuLink title="Canine (Dogs)" href="/browse/canine" onNavigate={close} />
            <MenuLink title="Feline (Cats)" href="/browse/feline" onNavigate={close} />
            <MenuLink title="Equine (Horses)" href="/browse/equine" onNavigate={close} />
            <MenuLink title="Cattle (Cows)" href="/browse/cattle" onNavigate={close} />
            <MenuLink title="Exotics" href="/browse/exotics" onNavigate={close} />
            <MenuLink title="Farm Animals" href="/browse/others" onNavigate={close} />
          </Section>
          
          <Section title="Adopt">
            <MenuLink title="All Adoptions" href="/adoptions" onNavigate={close} />
            <MenuLink title="Shelters &amp; Rescues" href="/shelters" onNavigate={close} />
          </Section>

          <Section title="Auctions">
            <MenuLink title="Live Auctions" href="/auctions" onNavigate={close} />
            <MenuLink title="My Auctions" href="/my/auctions" onNavigate={close} />
          </Section>

          <Section title="Breeding">
            <MenuLink title="Find a Sire" href="/browse/exotics/breeding/find?role=male" onNavigate={close} />
            <MenuLink title="Register as a Sire" href="/browse/exotics/breeding/register?role=male" onNavigate={close} />
            <MenuLink title="Find a Dam" href="/browse/exotics/breeding/find?role=female" onNavigate={close} />
            <MenuLink title="Register as a Dam" href="/browse/exotics/breeding/register?role=female" onNavigate={close} />

            <div className="h-px bg-white/10 my-2" />

            <MenuLink title="Dog Breeding" href="/browse/canine/breeding" onNavigate={close} />
            <MenuLink title="Cat Breeding" href="/browse/feline/breeding" onNavigate={close} />
            <MenuLink title="Horse Breeding" href="/browse/equine/breeding" onNavigate={close} />
            <MenuLink title="Cattle Breeding" href="/browse/cattle/breeding" onNavigate={close} />
            <MenuLink title="Exotics Breeding" href="/browse/exotics/breeding" onNavigate={close} />
          </Section>

          <Section title="Services">
            <MenuLink title="Browse Vendors" href="/vendors" onNavigate={close} />
            <MenuLink title="Transporters" href="/transporters" onNavigate={close} />
            <MenuLink title="Register as Vendor" href="/vendor/register" onNavigate={close} />
            <MenuLink title="Vendor Dashboard" href="/dashboard/vendor" onNavigate={close} />
          </Section>

          <Section title="Quick Links">
            <MenuLink title="Recently Added" href="/#recent" onNavigate={close} />
          </Section>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MenuDrawer;
